import React, { useEffect, useState } from "react";
import {
    BookOpen, Zap, Clock, Calendar, Settings, ChevronDown, ChevronUp,
    BarChart3, Brain, Coffee, TrendingUp, Award, Target, User2,
    Copy, Link, CheckCheck, UserPlus, X
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import { FaBell, FaClipboardCheck, FaFire, FaGraduationCap, FaSignOutAlt, FaStar, FaTrophy } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ParentDashboard = () => {
    const navigate = useNavigate();
    const [parentData, setParentData] = useState(null);
    const [children, setChildren] = useState([]);
    const [activeChild, setActiveChild] = useState(null);
    const [showAllChildren, setShowAllChildren] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [childStats, setChildStats] = useState({});
    const [recentActivity, setRecentActivity] = useState([]);
    const [childInsights, setChildInsights] = useState(null);

    // Invite link state
    const [inviteCode, setInviteCode] = useState("");
    const [codeCopied, setCodeCopied] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    // Add child modal state
    const [showAddChild, setShowAddChild] = useState(false);
    const [childIdInput, setChildIdInput] = useState("");
    const [addingChild, setAddingChild] = useState(false);
    const [addChildError, setAddChildError] = useState("");

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    const getGreetingEmoji = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "🌅";
        if (hour < 17) return "☀️";
        return "🌙";
    };

    // Fetch parent profile and children
    useEffect(() => {
        const fetchParentProfile = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/parents/me`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const data = await res.json();
                setParentData(data.parent);
                setChildren(data.children || []);
            } catch (err) {
                console.error("Error fetching parent profile:", err);
            }
        };
        fetchParentProfile();
    }, []);

    // Fetch parent invite code
    useEffect(() => {
        const fetchInviteInfo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/parents/invite-info`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                setInviteCode(data.code || "");
            } catch (err) {
                console.error("Error fetching invite info:", err);
            }
        };
        fetchInviteInfo();
    }, []);

    // Fetch selected child's stats
    useEffect(() => {
        if (!activeChild) return;
        const fetchChildStats = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/parents/child-stats/${activeChild.user_id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const data = await res.json();
                setChildStats(data.stats || {});
                setRecentActivity(data.recentActivity || []);
            } catch (err) {
                console.error("Error fetching child stats:", err);
            }
        };
        fetchChildStats();
    }, [activeChild]);

    // Fetch selected child's expanded insights (study frequency, lesson
    // completion, strengths/weaknesses, risk flag)
    useEffect(() => {
        if (!activeChild) return;
        const fetchChildInsights = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/parents/child-insights/${activeChild.user_id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const data = await res.json();
                setChildInsights(res.ok ? data.data : null);
            } catch (err) {
                console.error("Error fetching child insights:", err);
                setChildInsights(null);
            }
        };
        fetchChildInsights();
    }, [activeChild]);

    // Fetch notification count
    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;
                const res = await fetch(`${API_BASE_URL}/api/notifications`, {
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                setUnreadCount(data.data.filter(n => n.unread).length);
            } catch (err) {
                console.error("Error fetching notifications:", err);
            }
        };
        fetchUnreadCount();
    }, []);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/login";
    };

    const inviteLink = inviteCode
        ? `${window.location.origin}/join-parent?code=${inviteCode}`
        : "";

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const el = document.createElement("textarea");
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
        }
        if (type === "code") {
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        } else {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        }
    };

    const handleAddChild = async (e) => {
        e.preventDefault();
        setAddChildError("");
        if (!childIdInput.trim()) return;
        setAddingChild(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/parents/link-child`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({ student_id: childIdInput.trim() }),
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.message);
                setChildren(prev => [...prev, data.child]);
                setChildIdInput("");
                setShowAddChild(false);
            } else {
                setAddChildError(data.message || "Failed to add child.");
            }
        } catch (err) {
            setAddChildError("Network error. Please try again.");
        } finally {
            setAddingChild(false);
        }
    };

    const childSelected = (child) => {
        setActiveChild(child);
        localStorage.setItem("child_id", child.user_id);
        localStorage.setItem("student_id", child.user_id);
        localStorage.setItem("viewing_context", "parent");
        navigate("/child-stats");
    };

    const displayedChildren = showAllChildren ? children : children.slice(0, 4);

    const getScoreColor = (score) => {
        if (score >= 90) return "text-emerald-600 bg-emerald-50";
        if (score >= 80) return "text-green-600 bg-green-50";
        if (score >= 70) return "text-yellow-600 bg-yellow-50";
        if (score >= 60) return "text-orange-600 bg-orange-50";
        return "text-red-600 bg-red-50";
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'exam': return <Target className="w-4 h-4 text-red-500" />;
            case 'quiz': return <Brain className="w-4 h-4 text-purple-500" />;
            case 'assignment': return <BookOpen className="w-4 h-4 text-blue-500" />;
            default: return <Award className="w-4 h-4 text-green-500" />;
        }
    };

    return (
        <div className="min-h-screen">
            {/* Animated Background */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
            </div>

            {/* Header */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/20 shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2148C0] to-blue-600 bg-clip-text text-transparent flex items-center">
                                <FaGraduationCap className="mr-3 text-[#2148C0]" />
                                TeeChaa
                            </h1>
                            <div className="hidden md:flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-blue-700 font-medium">Parent</span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <div className="relative">
                                <FaBell
                                    onClick={() => navigate('/notification')}
                                    className="text-gray-600 text-lg cursor-pointer hover:text-[#2148C0] transition-colors"
                                />
                                {unreadCount > 0 && (
                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                        <span className="text-xs text-white font-bold">{unreadCount}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center space-x-3 bg-white rounded-full p-1 shadow-sm border">
                                <button
                                    onClick={() => navigate('/profile')}
                                    className="text-gray-700 hover:text-[#2148C0] px-3 py-2 rounded-full transition-colors duration-200 font-medium"
                                >
                                    <User2 />
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="text-gray-600 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-all duration-200"
                                >
                                    <FaSignOutAlt />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="relative z-10 container mx-auto px-6 py-8 max-w-7xl">

                {/* Welcome Banner */}
                <div className="mb-10 relative">
                    <div className="bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12"></div>
                        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-3xl md:text-4xl font-bold mb-2">
                                        {getGreeting()}, {parentData?.firstName || 'Parent'} {getGreetingEmoji()}
                                    </h2>
                                    <p className="text-blue-100 opacity-90">Monitor and celebrate your children's learning journey</p>
                                </div>
                                <div className="hidden md:block p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                                    <TrendingUp className="w-10 h-10 text-white" />
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-4 text-blue-100">
                                {localStorage.getItem("role_id") && (
                                    <div className="inline-flex items-center gap-3 bg-white/20 border border-white/30 rounded-xl px-4 py-2 backdrop-blur-sm">
                                        <span className="text-xs uppercase tracking-widest text-blue-100">Your ID</span>
                                        <span className="font-mono font-bold text-white text-base tracking-wider">{localStorage.getItem("role_id")}</span>
                                    </div>
                                )}
                                <div className="flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <Clock className="w-4 h-4" />
                                    <span className="font-medium">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                                <div className="flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <Calendar className="w-4 h-4" />
                                    <span className="font-medium">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Invite Link Section ─────────────────────────────────── */}
                <div className="mb-10">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-7 bg-gradient-to-b from-purple-500 to-blue-500 rounded-full mr-3"></div>
                        Connect Your Children
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Share invite link */}
                        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Invite Link</p>
                            <p className="text-xs text-gray-400 mb-3">Share this link with your child — they click it to get linked to your account</p>

                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-3">
                                <span className="font-mono text-xs text-gray-600 truncate flex-1">{inviteLink || "Loading…"}</span>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => copyToClipboard(inviteCode, "code")}
                                    className="flex items-center gap-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-[#2148C0] font-medium px-3 py-2 rounded-lg transition"
                                >
                                    {codeCopied ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {codeCopied ? "Copied!" : "Copy Code"}
                                </button>
                                <button
                                    onClick={() => copyToClipboard(inviteLink, "link")}
                                    className="flex items-center gap-1.5 text-sm bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium px-3 py-2 rounded-lg transition"
                                >
                                    {linkCopied ? <CheckCheck className="w-4 h-4" /> : <Link className="w-4 h-4" />}
                                    {linkCopied ? "Copied!" : "Copy Link"}
                                </button>
                            </div>
                        </div>

                        {/* Add by student ID */}
                        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
                            <div>
                                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Add by Student ID</p>
                                <p className="text-xs text-gray-400 mb-3">Enter your child's TSA ID (found on their account, e.g. TSA-847291)</p>
                            </div>
                            <button
                                onClick={() => { setShowAddChild(true); setAddChildError(""); setChildIdInput(""); }}
                                className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#2148C0] hover:bg-blue-700 text-white font-semibold rounded-xl transition"
                            >
                                <UserPlus className="w-4 h-4" />
                                Add Child
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Children Cards ──────────────────────────────────────── */}
                <div className="mb-12">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                        <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-3"></div>
                        Your Children
                        <span className="ml-3 text-sm font-normal text-gray-400">({children.length})</span>
                    </h3>

                    {children.length === 0 ? (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                            <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">No children linked yet</p>
                            <p className="text-gray-400 text-sm mt-1">Share your invite link or add by student ID above</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {displayedChildren.map((child, index) => (
                                <div
                                    key={child.user_id || index}
                                    onClick={() => childSelected(child)}
                                    className="group p-6 rounded-2xl cursor-pointer transition-all duration-300 hover:scale-105 bg-gradient-to-br from-[#2148C0]/10 to-blue-100 border border-blue-200 hover:shadow-lg"
                                >
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg bg-gradient-to-br from-[#2148C0] to-blue-500 shadow mb-4">
                                        {(child.firstName || "C")[0].toUpperCase()}
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-1">
                                        {child.firstName} {child.lastName}
                                    </h3>
                                    {child.school && <p className="text-gray-500 text-sm mb-1">{child.school}</p>}
                                    {child.yearGroup && <p className="text-gray-400 text-sm">Class: {child.yearGroup}</p>}
                                    {child.student_id && (
                                        <p className="text-xs text-blue-400 font-mono mt-2">{child.student_id}</p>
                                    )}
                                </div>
                            ))}

                            {children.length > 4 && (
                                <div className="col-span-full flex justify-center mt-4">
                                    <button
                                        onClick={() => setShowAllChildren(!showAllChildren)}
                                        className="inline-flex items-center text-[#2148C0] bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-all duration-300 px-6 py-3 rounded-xl font-medium"
                                    >
                                        {showAllChildren ? (
                                            <><ChevronUp className="mr-2 w-5 h-5" /> Show Less</>
                                        ) : (
                                            <><ChevronDown className="mr-2 w-5 h-5" /> Show {children.length - 4} More</>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Selected Child Stats */}
                {activeChild && (
                    <div className="mb-12">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                            <div className="w-2 h-8 bg-gradient-to-b from-green-500 to-blue-500 rounded-full mr-3"></div>
                            {activeChild.firstName}'s Performance
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-blue-500/20"><Clock className="w-6 h-6 text-blue-500" /></div>
                                    <TrendingUp className="w-5 h-5 text-blue-400" />
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Time Studied</p>
                                <h3 className="text-3xl font-bold text-gray-800">{childStats.total_hours || 0}<span className="text-lg text-gray-400">h</span></h3>
                            </div>

                            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-green-500/20"><FaFire className="w-6 h-6 text-green-500" /></div>
                                    <span className="text-green-500">🔥</span>
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Study Streak</p>
                                <h3 className="text-3xl font-bold text-gray-800">{childStats.streak || 0}<span className="text-lg text-gray-400"> day{(childStats.streak || 0) === 1 ? "" : "s"}</span></h3>
                            </div>

                            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-purple-500/20"><FaClipboardCheck className="w-6 h-6 text-purple-500" /></div>
                                    <Award className="w-5 h-5 text-purple-400" />
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Exams Completed</p>
                                <h3 className="text-3xl font-bold text-gray-800">{childStats.completed_tests || 0}</h3>
                            </div>

                            <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-100 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-yellow-500/20"><FaTrophy className="w-6 h-6 text-yellow-500" /></div>
                                    <FaStar className="w-5 h-5 text-yellow-400" />
                                </div>
                                <p className="text-gray-500 text-sm mb-1">Average Score</p>
                                <h3 className="text-3xl font-bold text-gray-800">{childStats.average_score || 0}<span className="text-lg text-gray-400">%</span></h3>
                            </div>
                        </div>

                        {/* Parent Intelligence: study frequency, lesson completion, strengths/weaknesses, risk flag */}
                        {childInsights && (
                            <div className="mt-8">
                                {childInsights.atRisk && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-4 mb-6 flex items-center gap-3">
                                        <span className="text-xl">⚠️</span>
                                        <p className="text-sm font-medium">
                                            {activeChild.firstName}'s scores have been trending down with low recent study activity — this may be a good time to check in.
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                        <p className="text-gray-500 text-sm mb-1">Study Frequency (30 days)</p>
                                        <h3 className="text-2xl font-bold text-gray-800">
                                            {childInsights.studyFrequency.activeDaysLast30d} active days
                                        </h3>
                                        <p className="text-sm text-gray-400 mt-1">
                                            {Math.round(childInsights.studyFrequency.minutesLast30d / 60)}h {childInsights.studyFrequency.minutesLast30d % 60}m studied
                                        </p>
                                    </div>
                                    <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                        <p className="text-gray-500 text-sm mb-1">Lesson Completion Rate</p>
                                        <h3 className="text-2xl font-bold text-gray-800">
                                            {childInsights.lessonCompletionRate != null ? `${childInsights.lessonCompletionRate}%` : "—"}
                                        </h3>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                        <h4 className="font-semibold text-gray-800 mb-3">Strengths</h4>
                                        {childInsights.strengths.length === 0 && <p className="text-sm text-gray-400">No data yet.</p>}
                                        {childInsights.strengths.map((s) => (
                                            <div key={s.subject} className="flex justify-between text-sm py-1">
                                                <span className="text-gray-600">{s.subject}</span>
                                                <span className="font-semibold text-green-600">{s.accuracy}%</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm">
                                        <h4 className="font-semibold text-gray-800 mb-3">Needs Support</h4>
                                        {childInsights.weaknesses.length === 0 && <p className="text-sm text-gray-400">No data yet.</p>}
                                        {childInsights.weaknesses.map((s) => (
                                            <div key={s.subject} className="flex justify-between text-sm py-1">
                                                <span className="text-gray-600">{s.subject}</span>
                                                <span className="font-semibold text-red-500">{s.accuracy}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-3 mt-6">
                                    <button
                                        onClick={() => navigate(`/readiness?studentId=${activeChild.user_id}`)}
                                        className="bg-[#2148C0] text-white px-5 py-2 rounded-xl font-semibold text-sm"
                                    >
                                        View Readiness
                                    </button>
                                    <button
                                        onClick={() => navigate(`/predictions?studentId=${activeChild.user_id}`)}
                                        className="bg-white border border-gray-200 text-gray-700 px-5 py-2 rounded-xl font-semibold text-sm"
                                    >
                                        View Predictions
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* ── Add Child Modal ─────────────────────────────────────────── */}
            {showAddChild && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-bold text-gray-800">Add a Child</h3>
                            <button onClick={() => setShowAddChild(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                            Enter your child's <span className="font-semibold text-[#2148C0]">TSA Student ID</span> — they can find it on their profile or the ID card shown when they first registered.
                        </p>

                        <form onSubmit={handleAddChild} className="space-y-4">
                            <input
                                type="text"
                                value={childIdInput}
                                onChange={e => { setChildIdInput(e.target.value); setAddChildError(""); }}
                                placeholder="e.g. TSA-847291"
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#2148C0]"
                                autoFocus
                            />

                            {addChildError && (
                                <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{addChildError}</p>
                            )}

                            <button
                                type="submit"
                                disabled={addingChild || !childIdInput.trim()}
                                className="w-full py-3 bg-[#2148C0] hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl transition flex items-center justify-center gap-2"
                            >
                                {addingChild ? (
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                ) : (
                                    <><UserPlus className="w-4 h-4" /> Add Child</>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
};

export default ParentDashboard;
